import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import ChatCarouselModal from "./ChatCarouselModal";
import { groupMessages } from "../../utils/chat/groupMessages";
import DateSeparator from "./DateSeparator";
import SystemMessage from "./SystemMessage";
import MessageGroup from "./MessageGroup";
import { useSelector } from "react-redux";
import { selectIdentity } from "../../selectors/userSelectors";

export default function MessageList({
  messages,
  typingUsersArray,
  hasOlder,
  loadingOlder,
  onLoadOlder,
}) {
  // R5.8-d: identity-only subscription via memoized selectIdentity. Previously
  // this component subscribed to the whole state.userLogin slice and read
  // userInfo.id once, propagating it as `currentUserId` to MessageGroup. The
  // whole-slice path re-rendered MessageList on every userLogin mutation
  // (token refresh, B2 spreads, profile patches, follow/unfollow). The
  // realtime message-arrival path (driven by the `messages` prop) is
  // unaffected — only the orthogonal userLogin-mutation axis is collapsed.
  const identity = useSelector(selectIdentity);
  const userId = identity?.userId ?? null;
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const previousScrollHeightRef = useRef(null);
  const previousLastMessageIdRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const lightboxTriggerRef = useRef(null);

  const lastMessageId = messages[messages.length - 1]?.id || null;

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    if (previousScrollHeightRef.current !== null) {
      const heightDiff = scrollEl.scrollHeight - previousScrollHeightRef.current;
      scrollEl.scrollTop += heightDiff;
      previousScrollHeightRef.current = null;
      previousLastMessageIdRef.current = lastMessageId;
      return;
    }

    const isFirstLoad = previousLastMessageIdRef.current === null;
    const lastMessageChanged = previousLastMessageIdRef.current !== lastMessageId;

    if ((isFirstLoad || lastMessageChanged) && stickToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }

    previousLastMessageIdRef.current = lastMessageId;
  }, [messages.length, lastMessageId]);

  const groupedMessages = useMemo(() => {
    return groupMessages(messages);
  }, [messages]);

  const handleScroll = () => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const distanceFromBottom =
      scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;

    stickToBottomRef.current = distanceFromBottom < 80;
  };

  const handleLoadOlder = () => {
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      previousScrollHeightRef.current = scrollEl.scrollHeight;
    }

    onLoadOlder?.();
  }

  return (
    <div className="chat-content" ref={scrollRef} onScroll={handleScroll}>
      {hasOlder && (
        <div className="load-older-messages">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={handleLoadOlder}
            disabled={loadingOlder}
          >
            {loadingOlder ? "Завантаження..." : "Показати старіші"}
          </button>
        </div>
      )}

      {groupedMessages.map((group, i) => {
        if (group.type === "date") {
          return (<DateSeparator key={i} date={group.date} />);
        }

        if (group.type === "system") {
          return (
            <SystemMessage
              key={i}
              message={group.messages[0]}
            />
          );
        }

        return (
          <MessageGroup
            key={i}
            group={group}
            currentUserId={userId}
            onImageClick={(id, trigger) => {
              lightboxTriggerRef.current = trigger || null;
              setSelectedImageId(id);
            }}
          />
        );
      })}

      {typingUsersArray.length > 0 && (
        <div className="user-writing">
          {typingUsersArray
            .map(u => u.firstname || u.email)
            .join(", ")}{" "}
          {typingUsersArray.length === 1 ? "друкує…" : "друкують…"}
        </div>
      )}

      <div ref={bottomRef} />

      <ChatCarouselModal
        messages={messages}
        selectedImageId={selectedImageId}
        restoreFocusRef={lightboxTriggerRef}
        onClose={() => setSelectedImageId(null)}
      />
    </div>
  );
}
